export const DEFAULT_INPUTS = {
  monthlySpend: 10000, roofSqFt: 30000, dollarsPerWatt: 2.5,
  roofWattsPerSqFt: 7, annualKWhPerKW: 1450, energyRate: 0.15,
  selfConsumption: 0.9, demandShare: 0.3, shaveFraction: 0.3,
  batteryCost: 80000, annualOperatingCostFraction: 0.015,
  taxRate: 0.21, creditRate: 0.3, firstYearDeductionFraction: 1,
  eligibilityConfirmed: false, taxBenefitsUsable: false, financing: "cash",
  annualFinancingPayment: 0, annualPpaPayment: 0,
  capRate: 0.065, ownerPaysPower: false,
};

/** Scenario arithmetic, not an engineered tariff or tax model. Never double-count ownership benefits in a PPA. */
export function calculateFinancials(x) {
  const rates = [x.selfConsumption, x.demandShare, x.shaveFraction, x.annualOperatingCostFraction,
    x.taxRate, x.creditRate, x.firstYearDeductionFraction];
  const amounts = [x.monthlySpend, x.roofSqFt, x.dollarsPerWatt, x.roofWattsPerSqFt,
    x.annualKWhPerKW, x.energyRate, x.batteryCost, x.annualFinancingPayment, x.annualPpaPayment];
  if (rates.some((v) => !Number.isFinite(v) || v < 0 || v > 1) ||
    amounts.some((v) => !Number.isFinite(v) || v < 0) ||
    !Number.isFinite(x.capRate) || x.capRate <= 0 || x.capRate > 1) throw new Error("Invalid financial inputs.");
  const annualBill = x.monthlySpend * 12;
  const energyBudget = annualBill * (1 - x.demandShare);
  const systemKW = Math.min(x.roofSqFt * x.roofWattsPerSqFt / 1000,
    energyBudget / Math.max(1, x.annualKWhPerKW * x.energyRate * x.selfConsumption));
  const storageIncluded = x.batteryCost > 0;
  const grossCost = systemKW * 1000 * x.dollarsPerWatt + x.batteryCost;
  const solarSavings = Math.min(energyBudget, systemKW * x.annualKWhPerKW * x.energyRate * x.selfConsumption);
  const demandSavings = storageIncluded ? annualBill * x.demandShare * x.shaveFraction : 0;
  const operatingCost = grossCost * x.annualOperatingCostFraction;
  const annualNetOperatingSavings = solarSavings + demandSavings - operatingCost;
  const owns = x.financing !== "ppa";
  const taxBenefitsApplied = owns && x.eligibilityConfirmed && x.taxBenefitsUsable;
  const credit = taxBenefitsApplied ? grossCost * x.creditRate : 0;
  const deductionBasis = Math.max(0, grossCost - credit * 0.5);
  const firstYearDeduction = taxBenefitsApplied ? deductionBasis * x.firstYearDeductionFraction : 0;
  const deductionTaxValue = firstYearDeduction * x.taxRate;
  const taxShield = credit + deductionTaxValue;
  const netCapital = grossCost - taxShield;
  const payment = x.financing === "cpace" ? x.annualFinancingPayment : x.financing === "ppa" ? x.annualPpaPayment : 0;
  const yearOneCashFlowBeforeTax = x.financing === "ppa"
    ? solarSavings + demandSavings - payment
    : annualNetOperatingSavings - payment;
  const annualAfterTaxSavings = annualNetOperatingSavings * (1 - x.taxRate);
  const simplePayback = x.financing === "cash" && annualAfterTaxSavings > 0 && netCapital > 0
    ? netCapital / annualAfterTaxSavings : null;
  const valuationSensitivity = x.ownerPaysPower && yearOneCashFlowBeforeTax > 0
    ? yearOneCashFlowBeforeTax / x.capRate : null;
  return { systemKW, grossCost, credit, deductionBasis, firstYearDeduction, deductionTaxValue,
    taxShield, solarSavings, demandSavings, operatingCost, annualNetOperatingSavings,
    annualAfterTaxSavings, yearOneCashFlowBeforeTax, netCapital, simplePayback,
    valuationSensitivity, taxBenefitsApplied };
}

export const money = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(n);
